import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Send, MapPin } from "lucide-react";
import { SUPPORT_EMAIL } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full rounded-2xl bg-mc-surface/60 border border-white/10 px-5 py-4 text-white placeholder:text-mc-muted/70 focus:outline-none focus:border-mc-pink/60 focus:ring-1 focus:ring-mc-pink/40 transition-all";

  return (
    <div className="relative bg-mc-bg min-h-screen pt-36 pb-32 px-6 lg:px-10 spotlight">
      <div className="mx-auto max-w-5xl grid lg:grid-cols-12 gap-14">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-mc-pink mb-5">Get in touch</p>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
            Let's talk <span className="text-gradient">live.</span>
          </h1>
          <p className="mt-6 text-lg text-mc-muted font-light leading-relaxed">
            Press, partnerships, or support — reach the MapCast team. We're building the world's
            AI-powered real-time location video platform and we'd love to hear from you.
          </p>
          <div className="mt-10 space-y-5">
            <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-4 group" data-testid="contact-email-link">
              <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-mc-violet/30 to-mc-pink/30 border border-white/10 flex items-center justify-center text-mc-pink"><Mail className="w-5 h-5" /></span>
              <span className="text-white/90 group-hover:text-white transition-colors">{SUPPORT_EMAIL}</span>
            </a>
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-mc-violet/30 to-mc-pink/30 border border-white/10 flex items-center justify-center text-mc-pink flex-shrink-0"><MapPin className="w-5 h-5" /></span>
              <address className="not-italic text-white/90 leading-relaxed" data-testid="contact-address">
                MapCast BV<br />
                Herengracht 320<br />
                1016 CE Amsterdam, The Netherlands
              </address>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 glass rounded-3xl p-8 md:p-10 space-y-5"
          data-testid="contact-form"
        >
          <div>
            <label className="font-mono text-xs uppercase tracking-[0.15em] text-mc-muted mb-2 block">Name</label>
            <input className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="contact-name-input" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-[0.15em] text-mc-muted mb-2 block">Email</label>
            <input type="email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" data-testid="contact-email-input" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-[0.15em] text-mc-muted mb-2 block">Message</label>
            <textarea rows={5} className={`${field} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us what's on your mind…" data-testid="contact-message-input" />
          </div>
          <button type="submit" disabled={sending} className="inline-flex items-center gap-2 rounded-full bg-white text-mc-bg font-semibold px-7 h-14 hover:scale-105 transition-transform duration-300 disabled:opacity-60 disabled:hover:scale-100" data-testid="contact-submit-btn">
            {sending ? "Sending…" : (<>Send message <Send className="w-4 h-4" /></>)}
          </button>
        </motion.form>
      </div>
    </div>
  );
}
